<template>
  <div class="intersport-search__grid">
    <div class="intersport-search js-popover-interpsort--first" data-bs-placement="bottom" :data-bs-content="translate('intersport.select-location-popover-text')">
      <div>
        <div class="intersport-search__title">{{ translate('intersport.pick-location-text') }}</div>
        <CustomSelect id="location-select"
                name="location-select"
                :label="''"
                :options="searchStore.getLocations()"
                :selected-option="selectedOption"
                @change-value="(event, isFirst) => SelectLocation(event, isFirst)"/>
      </div>
      <div class="intersport-search__bottom" v-if="Object.keys(searchStore.selectedLocation).length > 0">
        <a :href="searchStore.selectedLocation.detailLink" class="btn btn-underline btn-underline--text-sm">
          <span class="btn-underline__text">{{ translate('intersport.location-detail-link-text') }}</span>
          <span class="icon icon-arrow-right btn-underline__icon" aria-hidden="true"></span>
        </a>
        <a :href="searchStore.selectedLocation.mapLink" target="_blank" class="btn btn-underline btn-underline--text-sm">
          <span class="icon icon-map btn-underline__icon" aria-hidden="true"></span>
          <span class="btn-underline__text">{{ translate('intersport.location-map-link-text') }}</span>
        </a>
      </div>
    </div>

    <div :class="'intersport-search js-popover-interpsort--second' + (Object.keys(searchStore.selectedLocation).length === 0 ? ' disabled' : '')" data-bs-placement="bottom" :data-bs-content="translate('intersport.select-dates-popover-text')">
        <div>
          <div class="intersport-search__title">{{ translate('intersport.pick-date-text') }}</div>
            <div class="intersport-search__dates">
              <div class="d-flex">
                <div class="intersport-search__date-label">{{ translate('intersport.from') }}</div>
                <Datepicker id="from-date"
                            name="from-date"
                            class-name="form-group--bold"
                            :label="''"
                            :placeholder="translate('intersport.pick-date-label')"
                            :disable="toRaw(searchStore.disabledDates)"
                            :min-date="searchStore.generalMinDate"
                            :max-date="searchStore.generalMaxDate"
                            :key="[searchStore.disabledDates, searchStore.generalMinDate, searchStore.generalMaxDate].toString()"
                            :model-value="searchStore.startDate"
                            v-model="searchStore.startDate"/>
              </div>

              <div :class="'d-flex intersport-search__date-wrapper' + ((!searchStore.startDate && Object.keys(searchStore.selectedLocation).length > 0) ? ' disabled' : '')">
                <span class="intersport-search__date-label">{{ translate('intersport.to') }}</span>
                <Datepicker id="to-date"
                            name="to-date"
                            class-name="form-group--bold"
                            :label="''"
                            :placeholder="translate('intersport.pick-date-label')"
                            :disable="searchStore.disabledDates"
                            :min-date="searchStore.minDate"
                            :default-date="searchStore.startDate"
                            :max-date="searchStore.maxDate"
                            :key="[searchStore.disabledDates, searchStore.startDate, searchStore.minDate, searchStore.maxDate].toString()"
                            :model-value="searchStore.endDate"
                            v-model="searchStore.endDate"/>
              </div>
            </div>
        </div>

        <div :class="'intersport-search__bottom' + ((!searchStore.endDate && Object.keys(searchStore.selectedLocation).length > 0) ? ' disabled' : '')">
          <button class="btn-no-styling link" @click="searchProducts">
            {{ translate('intersport.search-button-text') }}
          </button>
        </div>
    </div>
  </div>

  <div class="modal fade" id="intersport-notification-modal" tabindex="-1" aria-labelledby="intersport-notification-modal" aria-hidden="true" ref="searchNotificationModal">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ translate('intersport.modal-notification-title') }}</h5>
          <button type="button"
                  class="btn btn-no-styling btn-close"
                  aria-label="Close"
                  data-bs-dismiss="modal">
            <span class="icon icon-close"></span>
          </button>
        </div>
        <div class="modal-body">
          {{ translate('intersport.modal-notification-text') }}
        </div>
        <div class="modal-footer">
          <button type="button"
                  class="btn btn-secondary btn-outline btn-icon-text order-2 order-md-1"
                  data-bs-dismiss="modal">
            <span class="icon icon-close btn-icon-text__icon" aria-hidden="true"></span>
            {{ translate('intersport.modal-cancel') }}
          </button>
          <button type="button"
                  class="btn btn-primary btn-icon-text order-1 order-md-2"
                  @click="reloadSearch">
            <span class="icon icon-check btn-icon-text__icon" aria-hidden="true"></span>
            {{ translate('intersport.modal-notification-confirm') }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <Toast :store="searchStore" />
  <Toast :store="productsStore" />
  <Toast :store="peopleStore" />
  <LiveToast :store="peopleStore" :toastTitle="translate('intersport.error-title')" />
  <LiveToast :store="productsStore" :toastTitle="translate('intersport.error-title')" />
</template>

<script setup lang="ts">
// @ts-ignore
import {ref, watch, defineProps, computed, onMounted, toRaw} from "vue";
// @ts-ignore
import { ISOStringToDate } from "@elements/date-utils";
// @ts-ignore
import {find} from "@elements/dom-utils";
// @ts-ignore
import {translate} from "@elements/translations";
// @ts-ignore
import Popover from "bootstrap/js/dist/popover";
// @ts-ignore
import Modal from "bootstrap/js/dist/modal";
import CustomSelect from "./components/form-elements/CustomSelect.vue";
import Datepicker from "./components/form-elements/Datepicker.vue";
import {useSearchStore} from "../stores/SearchStore.ts";
import {useProductsStore} from "../stores/ProductStore.ts";
import {usePeopleStore} from "../stores/PeopleStore.ts";
import { SearchStore } from "../types/intersport.ts";
import Toast from "./components/Toast.vue";
import LiveToast from "./components/LiveToast.vue";

const searchStore = useSearchStore();
const productsStore = useProductsStore();
const peopleStore = usePeopleStore();
const searchNotificationModal = ref();

const props = defineProps<{
  searchJson: SearchStore,
  productsRequestUrl: string
}>();

productsStore.setRequestUrl(props.productsRequestUrl);
searchStore.fillStore(props.searchJson);
let firstPopover: any;

const selectedOption = computed(() => {
  if (searchStore.selectedLocation) {
    return searchStore.selectedLocation.id;
  }

  return '';
});

const SelectLocation = (e: string, isFirstUpdate: boolean) => {
  if (!isFirstUpdate) {
    searchStore.setLocation(e);

    let secondPopover = Popover.getOrCreateInstance(find('.js-popover-interpsort--second'));
    firstPopover.enable();
    firstPopover.hide();
    firstPopover.disable();
    secondPopover.show();
    secondPopover.disable();
  }
}

onMounted(() => {
  firstPopover = Popover.getOrCreateInstance(find('.js-popover-interpsort--first'));
  firstPopover.show();
  firstPopover.disable();
});

async function searchProducts() {
  if(productsStore.getSelectedAvailabilities().length === 0) {
    await searchStore.searchProducts();
    await peopleStore.reloadPeople();
  } else {
    const modal = Modal.getOrCreateInstance(searchNotificationModal.value);
    modal.show();
  }
}

async function reloadSearch() {
  await searchStore.searchProducts(searchNotificationModal.value);
  await peopleStore.reloadPeople();
}
</script>